import { API } from "@/lib/api";

const audio = typeof Audio !== "undefined" ? new Audio() : null;
const SILENT = "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQxAADB8AhSmxhIIEVCSiJrDCQBTcu3UrAIwUdkRgQbFAZC1CQEwTJ9mjRvBA4UOLD8nKVOWfh+UlK3z/177OXrfOdKl7pyn3Xf//WreyTRUoAWgBgkOAGbZHBgG1OF6zM82DWbZaUmMBptgQhGjsyYqc9ae9XFz280948NMBWInljyzsNRFLPWdnZGWrddDsjK1unuSrVN9jJsK8KuQtQCtMBjCEtImISdNKJOopIpBFpNSMbIHCSRpRR5iakjTiyzLhchUUBwCgyKiweBv/7UsQbg8isVNoMPMjAAAA0gAAABEVFGmgqK////9bP/6XCykxBTUUzLjEwMKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq";
const subs = new Set();
let state = { speakingId: null, loading: false };
let finish = null;
let queue = [];
let playing = false;
let closed = false;
let gen = 0;
const done = () => { const f = finish; finish = null; if (f) f(); };
const set = (patch) => { state = { ...state, ...patch }; subs.forEach((f) => f(state)); };

export const tts = (text) => fetch(`${API}/voice/speak`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) })
  .then((r) => (r.ok ? r.blob() : null)).catch(() => null);

const release = () => {
  if (!audio) return;
  audio.pause();
  if (audio.src.startsWith("blob:")) URL.revokeObjectURL(audio.src);
  audio.removeAttribute("src");
};

const playNext = async (g) => {
  if (g !== gen) return;
  const item = queue.shift();
  if (!item) {
    playing = false;
    if (closed) { set({ speakingId: null, loading: false }); done(); }
    return;
  }
  playing = true;
  const blob = await item;
  if (g !== gen) return;
  if (!blob) return playNext(g);
  release();
  audio.src = URL.createObjectURL(blob);
  audio.onended = () => playNext(g);
  set({ loading: false });
  audio.play().catch(() => playNext(g));
};

// Split finished sentences off the front of text; returns [sentences, restIndex].
export const takeSentences = (text, from, minLen = 0) => {
  const re = /[.!?](?=\s)|\n/g;
  re.lastIndex = from;
  let end = -1;
  let m;
  while ((m = re.exec(text))) {
    end = m.index + 1;
    if (end - from >= minLen) break;
  }
  return end > from ? [text.slice(from, end).trim(), end] : ["", from];
};

export const speech = {
  subscribe(fn) { subs.add(fn); fn(state); return () => subs.delete(fn); },
  // Must run inside a user gesture (mic tap) so mobile browsers allow later playback.
  unlock() {
    if (!audio) return;
    audio.src = SILENT;
    audio.play().catch(() => {});
  },
  stop() {
    gen += 1;
    queue = [];
    playing = false;
    closed = false;
    release();
    set({ speakingId: null, loading: false });
    done();
  },
  // Streamed speech: open, push sentences as they arrive, then close. Resolves when the last clip ends or on stop().
  open(id) {
    this.stop();
    closed = false;
    set({ speakingId: id, loading: true });
    return new Promise((resolve) => { finish = resolve; });
  },
  push(text) {
    if (!audio || !text || !state.speakingId) return;
    queue.push(tts(text));
    if (!playing) playNext(gen);
  },
  pushBlob(blob) {
    if (!audio || !blob || !state.speakingId) return;
    queue.push(Promise.resolve(blob));
    if (!playing) playNext(gen);
  },
  close() {
    closed = true;
    if (!playing && !queue.length) { set({ speakingId: null, loading: false }); done(); }
  },
  speak(id, text) {
    if (!audio || !text) return Promise.resolve();
    const ended = this.open(id);
    let at = 0;
    let first = true;
    for (;;) {
      const [chunk, next] = takeSentences(text, at, first ? 0 : 220);
      if (!chunk) break;
      this.push(chunk);
      at = next;
      first = false;
    }
    const rest = text.slice(at).trim();
    if (rest) this.push(rest);
    this.close();
    return ended;
  },
};

const DISC = /(Commissions, management fees|Des commissions, des frais)/;
export const spokenBody = (text) => {
  const i = text.search(DISC);
  return i >= 0 ? text.slice(0, i) : text;
};
export const closingLine = (lang) => (lang === "fr" ? "L'avis de non-responsabilité complet est affiché à l'écran." : "The full disclaimer is shown on screen.");
export const spokenText = (text, lang) => {
  const i = text.search(DISC);
  const body = i > 0 ? text.slice(0, i) : text;
  return `${body.trim()} ${lang === "fr" ? "L'avis de non-responsabilité complet est affiché à l'écran." : "The full disclaimer is shown on screen."}`;
};
